import { DEFAULT_SOUND_VOLUMES } from './soundLevels';
export type SoundKind = 'rain' | 'stream' | 'wind';
export const SOUND_CATALOG = [
  { id: 'rain', tradition: null, language: null, reviewed: true, source: 'freesound-553390-cc0' },
  { id: 'stream', tradition: null, language: null, reviewed: true, source: 'freesound-559955-cc0' },
  { id: 'wind', tradition: null, language: null, reviewed: true, source: 'freesound-531016-cc0' },
  { id: 'bell', tradition: null, language: null, reviewed: true, source: 'procedural-web-audio' },
  { id: 'piano', tradition: null, language: null, reviewed: true, source: 'original-composition-salamander-cc-by-3.0' },
] as const;

class GardenAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private effects: GainNode | null = null;
  private gains = new Map<SoundKind, GainNode>();
  private sources = new Map<SoundKind, AudioBufferSourceNode>();
  private pending = new Map<SoundKind, Promise<void>>();
  private downloads = new Set<AbortController>();
  private onError: (() => void) | null = null;
  private enabled = false;
  private music: HTMLAudioElement | null = null;
  private musicSource: MediaElementAudioSourceNode | null = null;
  private musicGain: GainNode | null = null;
  private musicSelected = false;
  private musicVolume = 0.5;
  private volumes: Record<SoundKind, number> = { ...DEFAULT_SOUND_VOLUMES };

  async setEnabled(enabled: boolean) {
    // Set the media category only after the visitor explicitly enables sound.
    // Feature detection keeps browsers without the experimental API usable.
    {
      try { const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession; if (session && (enabled || session.type === 'playback')) session.type = enabled ? 'playback' : 'auto'; }
      catch { /* Playback still works normally when the platform declines the hint. */ }
    }
    if (enabled && !this.context) this.create();
    this.enabled = enabled;
    // Start both from the user's gesture; Safari may reject play() after awaiting resume().
    const musicPlay = enabled && this.musicSelected ? this.music?.play() : undefined;
    if (!enabled) this.music?.pause();
    if (enabled) await Promise.all([this.context?.resume(), musicPlay]);
    if (this.master && this.context) this.master.gain.setTargetAtTime(this.enabled ? 1 : 0, this.context.currentTime, 0.025);
    if (this.enabled) for (const kind of ['rain', 'stream', 'wind'] as const) if (this.volumes[kind] > 0) this.loadNature(kind);
  }
  async setMusicEnabled(selected: boolean) {
    if (!SOUND_CATALOG.find(item => item.id === 'piano')?.reviewed) return;
    this.musicSelected = selected;
    if (selected && !this.music) {
      if (!this.context) this.create();
      this.music = new Audio('/audio/veranda-03.mp3');
      this.music.preload = 'none'; this.music.loop = true;
      // iPhone ignores HTMLMediaElement.volume. A separate Web Audio gain controls the same looping element.
      this.musicSource = this.context!.createMediaElementSource(this.music);
      this.musicGain = this.context!.createGain(); this.musicGain.gain.value = this.musicVolume;
      this.musicSource.connect(this.musicGain); this.musicGain.connect(this.master!);
    }
    if (selected && this.enabled) {
      try { await Promise.all([this.context?.resume(), this.music?.play()]); }
      catch (error) { this.musicSelected = false; this.music?.pause(); throw error; }
    } else this.music?.pause();
  }
  setMusicVolume(value: number) {
    this.musicVolume = Math.max(0, Math.min(1, value));
    if (this.context) this.musicGain?.gain.setTargetAtTime(this.musicVolume, this.context.currentTime, 0.025);
  }
  private create() {
    this.context = new AudioContext();
    const ctx = this.context;
    this.master = ctx.createGain(); this.master.gain.value = 0; this.master.connect(ctx.destination);
    // Preserve the reviewed bell/nature level; the piano retains its previous independent volume range.
    this.effects = ctx.createGain(); this.effects.gain.value = 0.42; this.effects.connect(this.master);
  }
  setOnError(handler: (() => void) | null) { this.onError = handler; }
  /** Decode each licensed mono loop once. Context is resumed by the original gesture;
   * fetching a nature recording must not delay a bell or piano playback. */
  private loadNature(kind: SoundKind) {
    if (!this.context || this.sources.has(kind) || this.pending.has(kind) || !SOUND_CATALOG.find(item => item.id === kind)?.reviewed) return;
    const context = this.context, controller = new AbortController(); this.downloads.add(controller);
    const task = (async () => {
      try {
        const response = await fetch(`/audio/nature/${kind}-v1.mp3`, { signal: controller.signal });
        if (!response.ok) throw new Error('Nature recording unavailable');
        const buffer = await context.decodeAudioData(await response.arrayBuffer());
        if (this.context !== context) return;
        const source = context.createBufferSource(); source.buffer = buffer; source.loop = true;
        const gain = context.createGain(); gain.gain.value = this.volumes[kind];
        source.connect(gain); gain.connect(this.effects!); source.start();
        this.sources.set(kind, source); this.gains.set(kind, gain);
      } catch { if (!controller.signal.aborted && this.context === context) this.onError?.(); }
      finally { this.downloads.delete(controller); if (this.context === context) this.pending.delete(kind); }
    })();
    this.pending.set(kind, task);
  }
  setVolume(kind: SoundKind, value: number) {
    this.volumes[kind] = Math.max(0, Math.min(1, value));
    if (this.context) this.gains.get(kind)?.gain.setTargetAtTime(this.volumes[kind], this.context.currentTime, 0.2);
    if (this.enabled && this.volumes[kind] > 0) this.loadNature(kind);
  }
  /** `soft` is the lighter, shorter strike used at each prostration. */
  bell(strength: 'full' | 'soft' = 'full') {
    if (!this.enabled || !this.context || !this.master || !SOUND_CATALOG.find(item => item.id === 'bell')?.reviewed) return;
    const now = this.context.currentTime;
    const soft = strength === 'soft';
    const loudness = soft ? 0.55 : 1, length = soft ? 2.6 : 4.8, taper = soft ? 0.25 : 0.45;
    for (const [index, frequency] of [220, 443, 649, 906, 1213].entries()) {
      const oscillator = this.context.createOscillator(); oscillator.type = 'sine'; oscillator.frequency.value = frequency;
      const gain = this.context.createGain(); gain.gain.setValueAtTime(0, now); gain.gain.linearRampToValueAtTime(0.14 * loudness / (index + 1), now + 0.025); gain.gain.exponentialRampToValueAtTime(0.0001, now + length - index * taper);
      oscillator.connect(gain); gain.connect(this.effects!); oscillator.start(now); oscillator.stop(now + length + 0.2);
    }
  }
  beadClick() {
    if (!this.enabled || !this.context || !this.master) return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator(); oscillator.frequency.setValueAtTime(730, now); oscillator.frequency.exponentialRampToValueAtTime(260, now + 0.035);
    const gain = this.context.createGain(); gain.gain.setValueAtTime(0.025, now); gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
    oscillator.connect(gain); gain.connect(this.effects!); oscillator.start(now); oscillator.stop(now + 0.06);
  }
  async dispose() { for (const controller of this.downloads) controller.abort(); this.downloads.clear(); this.pending.clear(); for (const source of this.sources.values()) { source.stop(); source.disconnect(); } this.sources.clear(); this.music?.pause(); this.musicSource?.disconnect(); this.musicGain?.disconnect(); this.music = null; this.musicSource = null; this.musicGain = null; this.musicSelected = false; await this.context?.close(); this.context = null; this.gains.clear(); this.master = null; this.effects = null; this.enabled = false; }
}
export const audio = new GardenAudio();
